import type { MediaUploadError } from './../ricos-content/index.js';
import type { LinkPreviewData_Styles, TableCellData_BorderWidths } from './../ricos-schema/legacy/wix/rich_content/v1/index.js';
import type {
  AppEmbedData_CardStyles_Alignment,
  AppEmbedData_CardStyles_Layout,
  AppEmbedData_CardStyles_Type,
  AppEmbedData_ImageStyles_AspectRatio,
  AppEmbedData_ImageStyles_Position,
  AppEmbedData_ImageStyles_Resizing,
  AppEmbedData_RibbonStyles_Placement,
  ButtonData_Styles_ButtonSize,
} from './../ricos-schema/next-schema/generated/ricos_document.js';
import type {
  AppEmbedData_BookingData,
  AppEmbedData_EventData,
  AudioData,
  ButtonData_Styles,
  ButtonData_Type,
  CollapsibleListData_Direction,
  Decoration,
  DividerData_Alignment,
  DividerData_LineStyle,
  DividerData_Width,
  FileData_PDFSettings,
  FileSource,
  GalleryData_Item,
  GalleryOptions,
  GIF,
  GIFData_GIFType,
  HTMLData_Source,
  ImageData,
  LayoutData,
  Link,
  Media,
  Oembed,
  PlaybackOptions,
  PluginContainerData,
  PollData_Design,
  PollData_Layout,
  PollData_Poll,
  TableCellData_BorderColors,
  TableCellData_CellStyle,
  TextStyle,
  VideoData,
} from './../ricos-schema/next-schema/index.js';
import { Node_Type } from './../ricos-schema/next-schema/index.js';

export const PmNodeType = {
  DIVIDER: Node_Type.DIVIDER,
  IMAGE: Node_Type.IMAGE,
  CAPTION: Node_Type.CAPTION,
  GALLERY: Node_Type.GALLERY,
  FILE: Node_Type.FILE,
  GIF: Node_Type.GIF,
  VIDEO: Node_Type.VIDEO,
  AUDIO: Node_Type.AUDIO,
  LINK_PREVIEW: Node_Type.LINK_PREVIEW,
  EMBED: Node_Type.EMBED,
  POLL: Node_Type.POLL,
  APP_EMBED: Node_Type.APP_EMBED,
  COLLAPSIBLE_LIST: Node_Type.COLLAPSIBLE_LIST,
  COLLAPSIBLE_ITEM: Node_Type.COLLAPSIBLE_ITEM,
  COLLAPSIBLE_ITEM_TITLE: Node_Type.COLLAPSIBLE_ITEM_TITLE,
  COLLAPSIBLE_ITEM_BODY: Node_Type.COLLAPSIBLE_ITEM_BODY,
  HTML: Node_Type.HTML,
  CODE_BLOCK: Node_Type.CODE_BLOCK,
  TABLE: Node_Type.TABLE,
  TABLE_ROW: Node_Type.TABLE_ROW,
  TABLE_CELL: Node_Type.TABLE_CELL,
  FIGURE: 'FIGURE',
  LINK_BUTTON: 'LINK_BUTTON',
  ACTION_BUTTON: 'ACTION_BUTTON',
  TABLE_HEADER_CELL: 'TABLE_HEADER_CELL',
  PARAGRAPH: Node_Type.PARAGRAPH,
  HEADING: Node_Type.HEADING,
  BULLETED_LIST: Node_Type.BULLETED_LIST,
  ORDERED_LIST: Node_Type.ORDERED_LIST,
  LIST_ITEM: Node_Type.LIST_ITEM,
  BLOCKQUOTE: Node_Type.BLOCKQUOTE,
  LAYOUT: Node_Type.LAYOUT,
  LAYOUT_CELL: Node_Type.LAYOUT_CELL,
  SHAPE: Node_Type.SHAPE,
  text: 'text',
  hardbreak: 'hardbreak',
} as const;

export type PmNodeType = keyof typeof PmNodeType;

export type PmNode =
  | PmParagraphNode
  | PmTextNode
  | PmHeadingNode
  | PmBulletedListNode
  | PmOrderedListNode
  | PmListItemNode
  | PmBlockquoteNode
  | PmCodeBlockNode
  | PmVideoNode
  | PmDividerNode
  | PmFileNode
  | PmGalleryNode
  | PmGifNode
  | PmHtmlNode
  | PmImageNode
  | PmLinkPreviewNode
  | PmPollNode
  | PmAppEmbedNode
  | PmLinkButtonNode
  | PmActionButtonNode
  | PmCaptionNode
  | PmCollapsibleListNode
  | PmCollapsibleItemNode
  | PmCollapsibleItemTitleNode
  | PmCollapsibleItemBodyNode
  | PmTableNode
  | PmEmbedNode
  | PmTableCellNode
  | PmTableRowNode
  | PmAudioNode
  | PmShapeNode
  | PmFigureNode
  | PmLayoutNode
  | PmLayoutCellNode;

type DecorationToMark<T extends Decoration> = {
  type: T['type'];
  attrs: Omit<T, 'type'>;
};

export type PmParagraphNode = {
  type: typeof PmNodeType.PARAGRAPH;
  attrs: {
    id: string;
    indentation: number | null;
    level: number | null;
    textStyle: TextStyle | null;
    style: {} | null;
  };
  content?: PmNode[];
};

export type PmTextNode = {
  type: typeof PmNodeType.text;
  text: string;
  attrs?: {};
  marks?: DecorationToMark<Decoration>[];
};

export type PmHeadingNode = {
  type: typeof PmNodeType.HEADING;
  attrs: {
    id: string;
    indentation: number | null;
    level: number | null;
    textStyle: TextStyle | null;
    style: object | null;
  };
  content?: PmNode[];
};

type PmBulletedListNode = {
  type: typeof PmNodeType.BULLETED_LIST;
  attrs: {
    id: string;
    indentation: number;
    offset: number;
  };
  content?: PmNode[];
};

type PmOrderedListNode = {
  type: typeof PmNodeType.ORDERED_LIST;
  attrs: {
    id: string;
    indentation: number;
    offset: number;
    start: number;
  };
  content?: PmNode[];
};

export type PmListItemNode = {
  type: typeof PmNodeType.LIST_ITEM;
  attrs: {
    id: string;
  };
  content?: PmNode[];
};

type PmBlockquoteNode = {
  type: typeof PmNodeType.BLOCKQUOTE;
  attrs: {
    id: string;
    indentation: number | null;
    textStyle: TextStyle | null;
    paragraphId: string | null;
  };
  content?: PmNode[];
};

type PmCodeBlockNode = {
  type: typeof PmNodeType.CODE_BLOCK;
  attrs: {
    id: string;
    textStyle: TextStyle | null;
  };
  content?: PmNode[];
};

export type PmVideoNode = {
  type: typeof PmNodeType.VIDEO;
  attrs: {
    id: string;
    disableExpand: boolean | null;
    video: VideoData['video'];
    containerData: VideoData['containerData'];
    thumbnail: VideoData['thumbnail'];
    disableDownload: boolean | null;
    title: string | null;
    options: PlaybackOptions | null;
    loading: boolean;
    uploadToken: symbol | null;
    error: MediaUploadError | null;
    /// @deprecated
    loadingPercentage: number | null;
    tempData: object | null;
  };
  content?: PmNode[];
};

export type PmDividerNode = {
  type: typeof PmNodeType.DIVIDER;
  attrs: {
    id: string;
    containerData: PluginContainerData | null;
    lineStyle: DividerData_LineStyle | null;
    width: DividerData_Width | null;
    alignment: DividerData_Alignment | null;
  };
  content?: PmNode[];
};

export type PmFileNode = {
  type: typeof PmNodeType.FILE;
  attrs: {
    id: string;
    containerData: PluginContainerData | null;
    src: FileSource | null;
    name: string | null;
    type: string | null;
    size: number | null;
    sizeInKb: string | null;
    pdfSettings: FileData_PDFSettings | null;
    mimeType: string | null;
    path: string | null;
    loading: boolean;
    error: null | MediaError;
    tempData: boolean | null;
  };
  content?: PmNode[];
};

export type PmGalleryNode = {
  type: typeof PmNodeType.GALLERY;
  attrs: {
    id: string;
    items: (GalleryData_Item & { error?: MediaError; loading: boolean })[];
    containerData: PluginContainerData | null;
    options: GalleryOptions | null;
    disableExpand: boolean | null;
    disableDownload: boolean | null;
    loading: boolean;
    /// @deprecated
    loadingPercentage: number | null;
  };
  content?: PmNode[];
};

export type PmGifNode = {
  type: typeof PmNodeType.GIF;
  attrs: {
    id: string;
    containerData: PluginContainerData | null;
    original: GIF | null;
    downsized: GIF | null;
    height: number | null;
    width: number | null;
    gifType: GIFData_GIFType | null;
  };
  content?: PmNode[];
};

export type PmHtmlNode = {
  type: typeof PmNodeType.HTML;
  attrs: {
    id: string;
    isAdsense: boolean | null;
    containerData: PluginContainerData | null;
    source: HTMLData_Source | null;
    url: string | null;
    html: string | null;
    autoHeight: boolean;
    iframeHeight: number | null;
    state: PmHtmlNodeState | null;
  };
  content?: PmNode[];
};

type PmHtmlNodeState =
  | {
      status: 'INITIAL';
      prompt?: PromptState;
    }
  | {
      status: 'SUCCESS';
      prompt?: PromptState;
    }
  | {
      status: 'LOADING';
      prompt: PromptState;
      requestId: string;
    }
  | {
      status: 'ERROR';
      prompt: PromptState;
      error: string;
    };

type PromptState = {
  text: string;
  selectedWidgetTypeId?: string;
  fileRefs: Attachment[];
};

export type Attachment =
  | {
      id: string;
      status: 'ok';
      file: {
        name: string;
        type: FileType;
        url: string;
      };
    }
  | {
      id: string;
      status: 'loading';
      file: {
        name: string;
        type: FileType;
      };
    }
  | {
      id: string;
      status: 'error';
      file: {
        name: string;
        type: FileType;
      };
      error: string;
    };

type FileType = 'png' | 'jpg' | 'jpeg' | 'csv' | 'text' | 'pdf';

export type PmImageNode = {
  type: typeof PmNodeType.IMAGE;
  attrs: {
    id: string;
    image: ImageData['image'] | null;
    link: ImageData['link'] | null;
    disableExpand: boolean | null;
    altText: string | null;
    disableDownload: boolean | null;
    decorative: boolean | null;
    containerData: ImageData['containerData'] | null;
    loading: boolean | null;
    /// @deprecated
    loadingPercentage: number | null;
    tempData: string | null;
    error: null | MediaError;
    styles: {
      border: {
        color?: string;
        width?: number;
        radius?: number;
      };
    } | null;
  };
};
type MediaError = { key: string };

export type PmLinkPreviewNode = {
  type: typeof PmNodeType.LINK_PREVIEW;
  attrs: {
    id: string;
    containerData: PluginContainerData | null;
    link: Link | null;
    title: string | null;
    thumbnailUrl: string | null;
    description: string | null;
    html: string | null;
    styles: {
      backgroundColor?: string;
      titleColor?: string;
      subtitleColor?: string;
      linkColor?: string;
      borderWidth?: number;
      borderRadius?: number;
      borderColor?: string;
      thumbnailPosition?: LinkPreviewData_Styles['thumbnailPosition'];
    };
  };
  content?: PmNode[];
};

type PmPollNode = {
  type: typeof PmNodeType.POLL;
  attrs: {
    id: string;
    containerData: PluginContainerData | null;
    poll: PollData_Poll | null;
    layout: PollData_Layout | null;
    design: PollData_Design | null;
  };
  content?: PmNode[];
};

export type PmAppEmbedNode = {
  type: typeof PmNodeType.APP_EMBED;
  attrs: {
    id: string;
    containerData: PluginContainerData | null;
    type: string | null;
    name: string | null;
    url: string | null;
    image: Media | null;
    itemId: string | null;
    imageSrc: string | null;
    eventData: AppEmbedData_EventData | null;
    bookingData: AppEmbedData_BookingData | null;
    hideImage: boolean | null;
    hidePrice: boolean | null;
    hideDescription: boolean | null;
    hideDateTime: boolean | null;
    hideLocation: boolean | null;
    hideDuration: boolean | null;
    hideButton: boolean | null;
    hideRibbon: boolean | null;
    buttonStyles: AppEmbedData_ButtonStyles | null;
    imageStyles: AppEmbedData_ImageStyles | null;
    ribbonStyles: AppEmbedData_RibbonStyles | null;
    cardStyles: AppEmbedData_CardStyles | null;
    pricingData: AppEmbedData_PricingData | null;
  };
  content?: PmNode[];
};

export type AppEmbedData_ButtonStyles = {
  buttonText?: string;
  borderWidth?: number;
  borderRadius?: number;
  borderColor?: string;
  textColor?: string;
  backgroundColor?: string;
  borderColorHover?: string;
  textColorHover?: string;
  backgroundColorHover?: string;
  buttonSize?: ButtonData_Styles_ButtonSize;
};

export type AppEmbedData_ImageStyles = {
  imagePosition?: AppEmbedData_ImageStyles_Position;
  aspectRatio?: AppEmbedData_ImageStyles_AspectRatio;
  resizing?: AppEmbedData_ImageStyles_Resizing;
  borderColor?: string;
  borderWidth?: number;
  borderRadius?: number;
};

export type AppEmbedData_RibbonStyles = {
  ribbonText?: string;
  ribbonPlacement?: AppEmbedData_RibbonStyles_Placement;
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
  borderWidth?: number;
  borderRadius?: number;
};

export type AppEmbedData_CardStyles = {
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
  borderRadius?: number;
  type?: AppEmbedData_CardStyles_Type;
  alignment?: AppEmbedData_CardStyles_Alignment;
  titlePriceLayout?: AppEmbedData_CardStyles_Layout;
  titleColor?: string;
  textColor?: string;
};

export type AppEmbedData_PricingData = {
  valueFrom?: string;
  valueTo?: string;
  discountedValue?: string;
  currency?: string;
  pricingPlanId?: string;
};

export type PmLinkButtonNode = {
  type: typeof PmNodeType.LINK_BUTTON;
  attrs: {
    id: string;
    type: ButtonData_Type | null;
    text: string | null;
    containerData: PluginContainerData | null;
    styles: ButtonData_Styles | null;
    link: Link | null;
  };
  content?: PmNode[];
};

export type PmActionButtonNode = {
  type: typeof PmNodeType.ACTION_BUTTON;
  attrs: {
    id: string;
    type: ButtonData_Type | null;
    text: string | null;
    containerData: PluginContainerData | null;
    styles: ButtonData_Styles | null;
    link: Link | null;
  };
  content?: PmNode[];
};

export type PmCaptionNode = {
  type: typeof PmNodeType.CAPTION;
  attrs: {
    id: string;
    textStyle: TextStyle;
    styleDefaults: {
      paddingTop: string;
      paddingBottom: string;
    };
  };
  content?: PmTextNode[];
};

export type PmCollapsibleListNode = {
  type: typeof PmNodeType.COLLAPSIBLE_LIST;
  attrs: {
    id: string;
    containerData: PluginContainerData | null;
    expandOnlyOne: boolean;
    initialExpandedItems: 'FIRST' | 'ALL' | 'NONE';
    direction: CollapsibleListData_Direction;
    isQapageData: boolean | null;
  };
  content?: PmNode[];
};

export type PmCollapsibleItemNode = {
  type: typeof PmNodeType.COLLAPSIBLE_ITEM;
  attrs: {
    id: string;
    isExpanded: boolean;
  };
  content?: PmNode[];
};

type PmCollapsibleItemTitleNode = {
  type: typeof PmNodeType.COLLAPSIBLE_ITEM_TITLE;
  attrs: {
    id: string;
  };
  content?: PmNode[];
};

type PmCollapsibleItemBodyNode = {
  type: typeof PmNodeType.COLLAPSIBLE_ITEM_BODY;
  attrs: {
    id: string;
    isExpanded: boolean;
  };
  content?: PmNode[];
};

type PmTableNode = {
  type: typeof PmNodeType.TABLE;
  attrs: {
    id: string;
    containerData: null;
    dimensions: { colsWidthRatio: []; rowsHeight: []; colsMinWidth: [] } | null;
    header: null;
    rowHeader: null;
    columnHeader: null;
  };
  content?: PmNode[];
};

type PmTableCellNode = {
  type: typeof PmNodeType.TABLE_CELL;
  attrs: {
    id: string;
    cellStyle: TableCellData_CellStyle | null;
    borderColors: TableCellData_BorderColors | null;
    colspan: number;
    rowspan: number;
    colwidth: number[] | null;
    verticalAlignment: string;
    backgroundColor: string | null;
    borderWidths: TableCellData_BorderWidths | null;
  };
  content?: PmNode[];
};

type PmTableRowNode = {
  type: typeof PmNodeType.TABLE_ROW;
  attrs: {
    id: string;
    height: number;
  };
  content?: PmNode[];
};

type PmEmbedNode = {
  type: typeof PmNodeType.EMBED;
  attrs: {
    id: string;
    containerData: PluginContainerData | null;
    oembed: Oembed | null;
    src: string | null;
  };
  content?: PmNode[];
};

export type PmAudioNode = {
  type: typeof PmNodeType.AUDIO;
  attrs: {
    id: string;
    containerData: AudioData['containerData'] | null;
    audio: AudioData['audio'] | null;
    disableDownload: boolean | null;
    coverImage: AudioData['coverImage'] | null;
    name: string | null;
    authorName: string | null;
    html: string | null;
    error?: MediaUploadError;
    /// @deprecated
    tempData?: boolean;
    loading?: boolean;
  };
  content?: PmNode[];
};

export type PmShapeNode = {
  type: typeof PmNodeType.SHAPE;
  attrs: {
    id: string;
    shape: Media;
    containerData: PluginContainerData;
    styles?: {
      colors?: Record<string, string>;
    } | null;
  };
};

export type PmFigureNode = {
  type: typeof PmNodeType.FIGURE;
  attrs: {
    id: string;
  };
  content?: [PmImageNode | PmVideoNode | PmGifNode, PmCaptionNode];
};

export type PmLayoutNode = {
  type: typeof PmNodeType.LAYOUT;
  attrs: {
    id: string;
    backgroundColor?: string;
    backgroundImage?: LayoutData['backgroundImage'];
    borderColor?: string;
    borderWidth?: number;
    borderRadius?: number;
    backdropColor?: string;
    backdropImage?: LayoutData['backdropImage'];
    backdropPaddingTop?: number;
    backdropPaddingBottom?: number;
    gap?: number;
    cellPadding?: number[];
    responsivenessBehaviour?: LayoutData['responsivenessBehaviour'];
    responsivenessBreakpoint?: number;
    designTarget?: LayoutData['designTarget'];
    containerData: LayoutData['containerData'];
    banner?: LayoutData['banner'];
    /// @deprecated
    loading?: {
      backgroundImage?: boolean;
      backdropImage?: boolean;
    };
    error?: MediaUploadError;
  };
  content?: PmNode[];
};

export type PmLayoutCellNode = {
  type: typeof PmNodeType.LAYOUT_CELL;
  attrs: {
    id: string;
    colSpan?: number;
  };
  content?: PmNode[];
};
